import { RowDataSourceTableNumeric } from '@components-clean/core/study-data-filter/study-data-filter.interfaces';
import { ObservationType } from '@interfaces-clean/observations.interface';
import { components } from 'types/rest-schema';
import {
    SmActionsGridParams,
    SmObservationsGridParams,
    SmReviewLogGridParams,
    SubjectReviewActivityLogGridParams,
} from '@apis/bff-api/subject-monitoring';

export type GetStudyDataSetsParams = {
    studyId: string | null;
};

export type CM2Dataset = components['schemas']['DatasetDto'];

export type DatasetDetails = components['schemas']['DatasetDetails'];

export type DatasetAutomationDetails = components['schemas']['DatasetAutomationDetails'];

export type DatasetNotesDto = components['schemas']['DatasetNotesDto'];

export type DataProcessingLogDto = components['schemas']['DataProcessingLogDto'];

export type DatasetImportLogDto = components['schemas']['DatasetImportLogDto'];

export type StudyReviewDto = components['schemas']['DatasetReviewListDto'];

export type SiteReviewDto = components['schemas']['SiteDatasetReviewListDto'];

export type Widget = components['schemas']['ComparisonWidgetDto'];

export type SiteHubWidgets = components['schemas']['SiteHubWidgetsDto'];

export type FilterUICategoryConfig = components['schemas']['FilterUICategoryConfigDto'];

export type FilterUIValue = components['schemas']['FilterValueDto'];

export type FilterUICalculateFilters = components['schemas']['FilterUICalculateFiltersDTO'];

export type Site = components['schemas']['Site'];

export type ControlsHubWidgets = components['schemas']['ControlsHubWidgetsDto'];

export type ControlsHubPanelsAssociatedToRisk = components['schemas']['ControlsHubAssociatedPanelDto'];

export type ControlsHubChartData = components['schemas']['ControlsHubChartDataDto'];

export type ControlsHubPanelObservations = components['schemas']['ControlsHubPanelObservationsDto'];

export type ControlsHubPanelObservationReviewLogs = components['schemas']['ControlsHubPanelObservationReviewLogsDto'];

export type ControlsHubPanelObservationActions = components['schemas']['ControlsHubPanelObservationActionsDto'];

export enum FilterUIAction {
    Clear = 0,
    SelectAll = 1,
    UnselectAll = 2,
    Modify = 3,
    DatasetChange = 4,
    ClearAndApply = 5,
    ClearUnappliedChanges = 6,
}

export enum FilterUIExclusion {
    None = 0,
    Value = 1,
    Dataset = 2,
}

export type FilterUIClearArgs = {
    action: FilterUIAction.Clear | FilterUIAction.ClearAndApply | FilterUIAction.ClearUnappliedChanges;
};

export type FilterUIDatasetChangeArgs = {
    action: FilterUIAction.DatasetChange;
};

export type FilterUISelectAllArgs = {
    action: FilterUIAction.SelectAll | FilterUIAction.UnselectAll;
    context: RowDataSourceTableNumeric;
    fieldKey?: string;
};

export type FilterUIModifyArgs = {
    action: FilterUIAction.Modify;
    context: RowDataSourceTableNumeric;
    fieldKey?: string;
    checkedValueIds?: Array<number>;
    uncheckedValueIds?: Array<number>;
};

export type FilterUIArgs = FilterUIClearArgs | FilterUISelectAllArgs | FilterUIModifyArgs | FilterUIDatasetChangeArgs;

export type FilterUIStateUpdate = {
    context?: RowDataSourceTableNumeric;
    fieldKey?: string;
    checkedValueIds: Array<number>;
    uncheckedValueIds: Array<number>;
};

export type PreAppliedFilters = components['schemas']['SuppliedFilterUIDataDTO'];

export type StudyDashboardPanelData = components['schemas']['StudyDashboardPanelData'];

export type StudyDashboardPanelDataDataset = components['schemas']['StudyDashboardPanelDataDataset'];

export type CategoryConfig = components['schemas']['CategoryConfigDto'];

export type InfiniteScrollGridResponse = components['schemas']['GridResult'];

export type ColumnDefinition = components['schemas']['ColumnDefinition'];

export type SuccessResponse = components['schemas']['SuccessResponse'];

export type SuccessResponseWithMessage = components['schemas']['SuccessResponseWithMessage'];

export type PanelResult = components['schemas']['PanelResultDto'];

export type AdvanceFilter = components['schemas']['AdvanceFilter'];

export type GridSort = components['schemas']['Sort'];

export type DatasetReviewDto = components['schemas']['DatasetReviewDto'];

export type SiteDatasetReviewDto = components['schemas']['SiteDatasetReviewDto'];

export type ObsActionsLogsCountDto = components['schemas']['ObsActionsLogsCountDto'];

export type RadialChartSiteDto = components['schemas']['RadialChartSiteDto'];

export type RadialChartStudyDto = components['schemas']['RadialChartStudyDto'];

export type StudySiteDto = components['schemas']['StudySiteDto'];

export type PagingRequest = components['schemas']['PagingRequest'];

export type PanelDto = components['schemas']['PanelDto'];

export type SiteScoreDto = components['schemas']['SiteScoreDto'];

export type KRIReviewDto = components['schemas']['KriReviewDto'];

export type StudyPanelData = components['schemas']['StudyPanelData'];

export type StudyOverviewData = components['schemas']['StudyOverviewPanelDTO'];

export type StudyHubSiteGrid = components['schemas']['StudyHubSiteGridDto'];

export type StudyHubSiteGridData = components['schemas']['StudyHubSiteGridDataDto'];

export type StudyHubSiteGridPanelData = components['schemas']['StudyHubSiteGridPanelDataDto'];

export type StudyHubSiteGridPanelColumnData = components['schemas']['StudyHubSiteGridPanelColumnDataDto'];

export type StudyHubSiteGridWidgetColumnData = components['schemas']['StudyHubSiteGridWidgetColumnDataDto'];

export type StudyHubSiteGridWidgetData = components['schemas']['StudyHubSiteGridWidgetDataDto'];

export type SiteHubPanelScoreData = components['schemas']['SitePanelScoreData'];

export type SiteHubGridData = components['schemas']['SiteHubGridDataDto'];

export type AutomationAdd = components['schemas']['CreateAutomationModel'];

export type ActionsEmailPreviewDto = components['schemas']['ActionsEmailPreviewDto'];
export type EmailActionsModel = components['schemas']['EmailActionsModel'];

export type ReviewLogLookupDto = components['schemas']['ReviewLogLookupDto'];

export enum DatasetFilter {
    All = 0,
    Reviewed = 1,
    Unreviewed = 2,
}

export type DownloadFileResult = {
    fileName: string | null;
    blob: Blob | null;
};

export type ExportColumn = {
    key: number;
    fieldName: string;
    children: ExportColumn[] | undefined;
};

export type ExportExcelArgs = {
    filters?: { filters: AdvanceFilter[] | null } | null;
    sort?: GridSort | null;
    columns: number[];
    sessionId?: string;
    advancedColumns?: ExportColumn[];
};

/**
 * Defines the types that can be used for API grid parameters. It must conform to one of the given types
 */
export type ApiGridParams =
    // CM
    | CmObservationsGridParams
    | CmActionsGridParams
    | CmReviewLogGridParams
    | DataTableGridParams
    | ControlsHubPanelGridParams
    | ControlsHubOoTGridParams
    // SM
    | SmObservationsGridParams
    | SmReviewLogGridParams
    | SmActionsGridParams
    | SubjectReviewActivityLogGridParams
    | SMDataTableGridParams;

export type CmObservationsGridParams = {
    grid: 'Observations';
    level: ObservationType;
    studyRef: string | null;
    observationId: string | null;
    isChild: boolean | null;
};

export type CmActionsGridParams = {
    grid: 'Actions';
    level: ObservationType;
    studyRef: string | null;
    observationId: string | null;
    reviewLogId: string | null;
    isChild: boolean | null;
};

export type CmReviewLogGridParams = {
    grid: 'ReviewLog';
    level: ObservationType;
    studyRef: string | null;
    observationId: string | null;
    reviewLogId: string | null;
    isChild: boolean | null;
    actionChild?: boolean | null;
    actionId?: string | null;
    isScatterPlot?: boolean | null;
    isRateGroup?: boolean | null;
    isHistory: boolean | null;
};

export type DataTableGridParams = {
    grid: 'DataTable';
    studyRef: string | null;
    dataset: number | null;
    datatable?: string | null;
    isChild?: boolean | null;
    subjectId?: string | null;
    isSubjectLevelTable?: boolean | null;
    comparisonDatasetId?: number | null;
    dataViewRef?: string | null;
    subjectsOnReviewId?: number | null;
    subjectIsOnAReview?: boolean | null;
};

export type SMDataTableGridParams = {
    grid: 'SMDataTable';
    studyRef: string;
    dataset: number;
    datatable: string;
    subjectId: string;
    isSubjectLevelTable: boolean;
    reviewRecordId?: number;
};

export type ControlsHubPanelGridParams = {
    grid: 'ControlsHubPanelGrid';
    studyRef: string;
    datasetId: number;
    panelRef: string;
    country?: string;
    isChild: false;
};

export type ControlsHubOoTGridParams = {
    grid: 'ControlsHubOoTGrid';
    studyRef: string;
    country?: string;
    isChild: false;
};

export type ObservationRecord = {
    id?: string;
    observation_id?: string;
    open_actions: number;
    reviews: number;
    is_scatter_plot: string;
    record_id: string;
    observation_record_id: string;
    is_poisson_or_negative_binomial: string;
    closed_date: string | null;
    type: string;
};

export type AbortablePagingRequst = {
    signal?: AbortSignal;
} & PagingRequest;

export type MultiValueType = components['schemas']['MultiValueType'];
export enum MultiValueTypeEnum {
    Previous = 0,
    Current = 1,
    Direction = 2,
    PreviousOrCurrent = 3,
}

export type RAGType = components['schemas']['RAGType'];
export enum RAGTypeEnum {
    Value = 0,
    Colour = 1,
}
